import {
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useState, useEffect } from "react";
import { useGetCategoriesQuery } from "slices/beautyApp/categorySlice";
import {
  useGetCategoryBasedServicesMutation,
  useAddPackageMutation,
  useUpdatePackageMutation,
  useGetSinglePackageQuery,
} from "slices/beautyApp/packageSlice";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { uploadImage } from "services/imageUpload";
import { IMAGE_URL } from "network";
import Points from "./points";

function AddPackage() {
  const { id } = useParams();
  const naviagte = useNavigate();
  const { data: categoryResp } = useGetCategoriesQuery({});
  const [getServices] = useGetCategoryBasedServicesMutation();
  const isEdit = Boolean(id);

  const { data: initialData, isLoading: isLoadingInitial } =
    useGetSinglePackageQuery(id, {
      skip: !isEdit,
    });
  const [add] = useAddPackageMutation();
  const [update] = useUpdatePackageMutation();
  const [serviceTypesList, setServiceTypesList] = useState([]);
  const [formData, setFormData] = useState({
    category: "",
    name: "",
    sub_title: "",
    off_percentage: "",
    image: "",
    metaTitle: "",
    metaDescription: "",
    keywords: [],
    serviceTypes: [], // [{ id, services: [{ title, duration, products: [{name, price}] }] }]
    sub_points: [],
    isAvailable: true,
  });

  const [previewUrl, setPreviewUrl] = useState("");
  const [keywordInput, setKeywordInput] = useState("");

  useEffect(() => {
    if (initialData?.data) {
      setFormData({
        ...initialData.data,
        keywords: initialData.data.keywords || [],
        serviceTypes: initialData.data.connections || [],
      });
      if (initialData.data.image) {
        setPreviewUrl(IMAGE_URL + initialData.data.image);
      }
    }
  }, [initialData]);


  // Fetch services when category changes
  useEffect(() => {
    async function fetchServices() {
      if (formData.category) {
        try {
          const resp = await getServices(formData.category).unwrap();
          setServiceTypesList(resp.data || []);

          // ✅ Only reset serviceTypes if it's NOT in edit mode
          if (!isEdit) {
            setFormData((prev) => ({ ...prev, serviceTypes: [] }));
          }
        } catch (err) {
          console.error(err);
          setServiceTypesList([]);
        }
      }
    }

    fetchServices();
  }, [formData.category, getServices]);

  const handleCategoryChange = (e) =>
    setFormData((prev) => ({ ...prev, category: e.target.value }));

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageName = await uploadImage(file);
      setPreviewUrl(IMAGE_URL + imageName);
      setFormData((prev) => ({ ...prev, image: imageName }));
    }
  };

  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (kw && !formData.keywords.includes(kw)) {
      setFormData((prev) => ({ ...prev, keywords: [...prev.keywords, kw] }));
      setKeywordInput("");
    }
  };

  const removeKeyword = (kw) =>
    setFormData((prev) => ({
      ...prev,
      keywords: prev.keywords.filter((k) => k !== kw),
    }));

  // Toggle service type selection
  const handleServiceToggle = (srvId) => {
    setFormData((prev) => {
      const exists = prev.serviceTypes.find((s) => s.serviceType === srvId);
      if (exists) {
        // Remove service type
        return {
          ...prev,
          serviceTypes: prev.serviceTypes.filter(
            (s) => s.serviceType !== srvId
          ),
        };
      }
      // Add new service type with empty services array
      return {
        ...prev,
        serviceTypes: [
          ...prev.serviceTypes,
          { serviceType: srvId, services: [] },
        ],
      };
    });
  };

  // Add a new service entry (title/duration/products) inside a service type
  const addServiceEntry = (srvId) => {
    setFormData((prev) => ({
      ...prev,
      serviceTypes: prev.serviceTypes.map((s) =>
        s.serviceType === srvId
          ? {
              ...s,
              services: [
                ...(s.services || []),
                { title: "", duration: "", products: [] },
              ],
            }
          : s
      ),
    }));
  };

  // Update title or duration of a service entry
  const updateServiceField = (srvId, serviceIndex, key, value) => {
    setFormData((prev) => ({
      ...prev,
      serviceTypes: prev.serviceTypes.map((s) => {
        if (s.serviceType === srvId) {
          const updatedServices = s.services.map((service, i) =>
            i === serviceIndex ? { ...service, [key]: value } : service
          );
          return { ...s, services: updatedServices };
        }
        return s;
      }),
    }));
  };

  // Add a product to a service entry
  const addProductToService = (srvId, serviceIndex) => {
    setFormData((prev) => ({
      ...prev,
      serviceTypes: prev.serviceTypes.map((s) => {
        if (s.serviceType === srvId) {
          const updatedServices = s.services.map((service, i) => {
            if (i === serviceIndex) {
              return {
                ...service,
                products: [
                  ...(service.products || []),
                  { name: "", price: "" },
                ],
              };
            }
            return service;
          });
          return { ...s, services: updatedServices };
        }
        return s;
      }),
    }));
  };

  // Update a product field (name or price)
  const updateProduct = (srvId, serviceIndex, productIndex, key, value) => {
    setFormData((prev) => ({
      ...prev,
      serviceTypes: prev.serviceTypes.map((s) => {
        if (s.serviceType === srvId) {
          const updatedServices = s.services.map((service, i) => {
            if (i === serviceIndex) {
              const updatedProducts = service.products.map((product, pIndex) =>
                pIndex === productIndex ? { ...product, [key]: value } : product
              );
              return { ...service, products: updatedProducts };
            }
            return service;
          });
          return { ...s, services: updatedServices };
        }
        return s;
      }),
    }));
  };

  // Remove a product from service entry
  const removeProduct = (srvId, serviceIndex, productIndex) => {
    setFormData((prev) => ({
      ...prev,
      serviceTypes: prev.serviceTypes.map((s) => {
        if (s.serviceType === srvId) {
          const updatedServices = s.services.map((service, i) => {
            if (i === serviceIndex) {
              const updatedProducts = service.products.filter(
                (_, pIndex) => pIndex !== productIndex
              );
              return { ...service, products: updatedProducts };
            }
            return service;
          });
          return { ...s, services: updatedServices };
        }
        return s;
      }),
    }));
  };

  // Remove a service entry inside a service type
  const removeServiceEntry = (srvId, serviceIndex) => {
    setFormData((prev) => ({
      ...prev,
      serviceTypes: prev.serviceTypes.map((s) => {
        if (s.serviceType === srvId) {
          const updatedServices = s.services.filter(
            (_, i) => i !== serviceIndex
          );
          return { ...s, services: updatedServices };
        }
        return s;
      }),
    }));
  };

  const handleSectionChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        await update({ id, body: formData }).unwrap();
      } else {
        await add(formData).unwrap();
      }
      naviagte("/beauty-app/packages"); // redirect after success
    } catch (err) {
      console.log("errr", err);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
        <Typography variant="h4" gutterBottom>
          {initialData ? "Edit Package" : "Add Package"}
        </Typography>

        <Paper elevation={2} sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            {/* Category */}
            <FormControl fullWidth margin="normal" required>
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"
                label="Category"
                value={formData.category}
                onChange={handleCategoryChange}
              >
                {categoryResp?.data
                  ?.filter((c) => c.package === "active")
                  ?.map((c) => (
                    <MenuItem key={c._id} value={c._id}>
                      {c.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {/* Basic Info */}
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isAvailable === true}
                  onChange={(e) =>
                    handleFieldChange("isAvailable", e.target.checked)
                  }
                />
              }
              label="Package status (Active/Inactive)"
            />

            <TextField
              label="Title"
              value={formData.name}
              onChange={(e) => handleFieldChange("name", e.target.value)}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="Sub Title"
              value={formData.sub_title}
              onChange={(e) => handleFieldChange("sub_title", e.target.value)}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              type="number"
              label="Off Percentage"
              value={formData.off_percentage}
              onChange={(e) =>
                handleFieldChange("off_percentage", e.target.value)
              }
              fullWidth
              margin="normal"
              required
            />

            {/* Image Upload */}
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Image Upload
            </Typography>
            <Box mt={1}>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                required={!initialData}
              />
              {previewUrl && (
                <Box mt={2}>
                  <img
                    crossorigin="anonymous"
                    src={previewUrl}
                    alt="Preview"
                    style={{ width: 140, borderRadius: 8 }}
                  />
                </Box>
              )}
            </Box>

            <Points
              subData={formData.sub_points}
              onChange={(value) => handleSectionChange("sub_points", value)}
            />

            {/* SEO */}
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              SEO Metadata
            </Typography>

            <TextField
              label="Meta Title"
              value={formData.metaTitle}
              onChange={(e) => handleFieldChange("metaTitle", e.target.value)}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="Meta Description"
              value={formData.metaDescription}
              onChange={(e) =>
                handleFieldChange("metaDescription", e.target.value)
              }
              fullWidth
              margin="normal"
              required
            />

            {/* Keywords */}
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Keywords
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <TextField
                label="Add Keyword"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addKeyword();
                  }
                }}
                fullWidth
              />
              <Button onClick={addKeyword} variant="contained">
                Add
              </Button>
            </Box>
            <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
              {formData.keywords.map((k) => (
                <Chip key={k} label={k} onDelete={() => removeKeyword(k)} />
              ))}
            </Box>

            {/* Service Types */}
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Service Types
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: 2,
              }}
            >
              {serviceTypesList.map((srv) => {
                const selected = formData.serviceTypes.find(
                  (s) => s.serviceType === srv._id
                );
                return (
                  <Paper key={srv._id} sx={{ p: 2 }} variant="outlined">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!selected}
                          onChange={() => handleServiceToggle(srv._id)}
                        />
                      }
                      label={srv.name}
                    />
                    {selected && (
                      <Box>
                        {selected.services?.map((service, sIndex) => (
                          <Paper
                            key={sIndex}
                            variant="outlined"
                            sx={{ p: 2, mt: 2 }}
                          >
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography variant="subtitle2">
                                Service #{sIndex + 1}
                              </Typography>
                              <Button
                                size="small"
                                color="error"
                                onClick={() =>
                                  removeServiceEntry(srv._id, sIndex)
                                }
                              >
                                Remove
                              </Button>
                            </Box>
                            <TextField
                              label="Service Title"
                              value={service.name}
                              onChange={(e) =>
                                updateServiceField(
                                  srv._id,
                                  sIndex,
                                  "name",
                                  e.target.value
                                )
                              }
                              fullWidth
                              margin="dense"
                              required
                            />
                            <TextField
                              label="Duration (min)"
                              type="number"
                              value={service.duration}
                              onChange={(e) =>
                                updateServiceField(
                                  srv._id,
                                  sIndex,
                                  "duration",
                                  e.target.value
                                )
                              }
                              fullWidth
                              margin="dense"
                              required
                            />

                            {/* Products */}
                            <Box mt={2}>
                              <Typography variant="body2" fontWeight="bold">
                                Products
                              </Typography>
                              {service.products?.map((product, pIndex) => (
                                <Box
                                  key={pIndex}
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  mt={1}
                                >
                                  <TextField
                                    label="Product Name"
                                    value={product.name}
                                    onChange={(e) =>
                                      updateProduct(
                                        srv._id,
                                        sIndex,
                                        pIndex,
                                        "name",
                                        e.target.value
                                      )
                                    }
                                    fullWidth
                                    required
                                  />
                                  <TextField
                                    label="Price"
                                    type="number"
                                    value={product.price}
                                    onChange={(e) =>
                                      updateProduct(
                                        srv._id,
                                        sIndex,
                                        pIndex,
                                        "price",
                                        e.target.value
                                      )
                                    }
                                    sx={{ width: 120 }}
                                    required
                                  />
                                  <IconButton
                                    onClick={() =>
                                      removeProduct(srv._id, sIndex, pIndex)
                                    }
                                    color="error"
                                    size="small"
                                  >
                                    <Delete />
                                  </IconButton>
                                </Box>
                              ))}
                              <Button
                                onClick={() =>
                                  addProductToService(srv._id, sIndex)
                                }
                                startIcon={<Add />}
                                sx={{ mt: 1 }}
                                size="small"
                              >
                                Add Product
                              </Button>
                            </Box>
                          </Paper>
                        ))}

                        <Button
                          onClick={() => addServiceEntry(srv._id)}
                          startIcon={<Add />}
                          // variant="outlined"
                          fullWidth
                          size="small"
                          sx={{ mt: 2 }}
                          color="primary" //
                        >
                          Add Service Entry
                        </Button>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Box>

            {/* Submit */}
            <Box mt={4}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
              >
                {initialData ? "Update Package" : "Create Package"}
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>
    </DashboardLayout>
  );
}

export default AddPackage;
